import {useMemo, useState} from "react";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import {isAfter, parseISO} from "date-fns";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {
    backgroundColor,
    borderRadius,
    calculateRemSize,
    flex,
    flexAbsoluteCenter,
    flexDirection,
    mb,
    ml,
    mr,
    mt,
    mv,
    p,
    pb,
    pr,
    pt,
    w100
} from "@pg-design/helpers-css";
import {CalendarCheckIcon, ChevronLeftIcon, ChevronRightIcon, FloorIcon} from "@pg-design/icons-module";
import {Placeholder} from "@pg-design/image-module";
import {Highlight, Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, dateTimeFormat, formatDate, pluralize, priceFormat} from "@pg-mono/string-utils";

import {ApplicationSourceSection} from "../../application/utils/ApplicationSource";
import {PropertyListApplicationButton} from "../../property_list/components/property_list_application_button/PropertyListApplicationButton";
import {GTMEvent, propertyConfiguratorAnalytics} from "../analytics/events";
import {IPropertyConfiguratorProperty} from "../types/IPropertyConfiguratorProperty";
import {AdditionalAreaTypeNames} from "../types/RoomType";

interface IOwnProps {
    property: IPropertyConfiguratorProperty;
}

interface IProps extends IOwnProps {}

export const HorizontalPropertyBox = (props: IProps) => {
    const {property} = props;
    const roomsPluralize = pluralize(["pokój", "pokoje", "pokoi"]);
    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: property.offer.vendor.slug,
        offerSlug: property.offer.slug,
        offerId: property.offer.id,
        propertyId: property.id
    });

    const mappedAdditionalAreas = useMemo(() => {
        if (!property.additional_areas.length) {
            return "";
        }

        const additionalAreas = property.additional_areas
            .filter((area) => !!area.area)
            .map((area) => `${AdditionalAreaTypeNames[area.type - 1]} ${area.area}m²`);
        return ` + ${additionalAreas.join(" + ")}`;
    }, []);

    const externalPlanUrl = property?.external_plan_url;
    const mainImage = externalPlanUrl || property.plan_image?.p_img_375x250;
    const [imageIndex, setImageIndex] = useState(0);

    const gallery = useMemo(() => {
        if (!property.gallery.length && !mainImage) {
            return [];
        }
        if (!property.gallery.length) {
            return [mainImage];
        }
        return [mainImage, ...property.gallery.map((image) => image.image.g_img_500)];
    }, []);

    const handleIndexChange = (value: number) => {
        if (imageIndex + value > gallery.length - 1) {
            return setImageIndex(0);
        }
        if (imageIndex + value === -1) {
            return setImageIndex(gallery.length - 1);
        }
        setImageIndex((prev) => prev + value);
    };

    const isBuild = isAfter(new Date(), parseISO(property.offer.construction_date_range?.upper as string));
    const date = formatDate(property.offer.construction_date_range?.upper, dateTimeFormat.quarter);

    return (
        <a href={propertyLink} target="_blank" css={wrapper}>
            <ImageWrapper hasImage={!!mainImage}>
                {gallery.length > 1 && (
                    <ChevronLeftIcon
                        onClick={(e) => {
                            e.preventDefault();
                            handleIndexChange(-1);
                        }}
                        wrapperSize="2.4"
                        size="2.4"
                        wrapperColor="transparent"
                    />
                )}
                {!!gallery.length ? <img src={gallery[imageIndex]} alt="plan mieszkania" /> : <Placeholder />}
                {gallery.length > 1 && (
                    <ChevronRightIcon
                        onClick={(e) => {
                            e.preventDefault();
                            handleIndexChange(1);
                        }}
                        wrapperSize="2.4"
                        size="2.4"
                        wrapperColor="transparent"
                    />
                )}
            </ImageWrapper>
            <div css={propertyTextContainer}>
                <Text variant="headline_5" css={mb(0.5)}>
                    {property.rooms} {roomsPluralize(property.rooms)}, {areaFormat(property.area)}
                    {mappedAdditionalAreas}
                </Text>
                <Text variant="info_txt_1">{property.offer.name}</Text>
                <Text variant="info_txt_1">{property.offer.address}</Text>
                <div css={specsContainer}>
                    <div css={iconContainer}>
                        <CalendarCheckIcon wrapperSize="2.4" size="2.4" wrapperColor="transparent" />
                        <Text variant="info_txt_1" css={[ml(0.5), mr(2)]}>
                            {isBuild ? "Gotowe do odbioru" : date}
                        </Text>
                        <div css={tooltipStyle}>Termin oddania: {isBuild ? "Gotowe do odbioru" : date}</div>
                    </div>
                    <div css={iconContainer}>
                        <FloorIcon wrapperSize="2.4" size="2.4" wrapperColor="transparent" />
                        <Text variant="info_txt_1" css={ml(0.5)}>
                            {property.floor || "Parter"}
                        </Text>
                        <div css={tooltipStyle}>Piętro: {property.floor || "Parter"}</div>
                    </div>
                </div>
                {!!property.price && (
                    <Text variant="info_txt_1" css={[pb(2)]}>
                        <Highlight>
                            <Text variant="headline_1" as="span">
                                {priceFormat(property.price)}&nbsp;
                            </Text>
                        </Highlight>
                    </Text>
                )}
                <div css={buttonsWrapper}>
                    <PropertyListApplicationButton
                        property={property}
                        isAutoresponderEnabled={property.offer.configuration.is_autoresponder_enabled}
                        vendorId={property.offer.vendor.id}
                        isInPropertyConfigurator
                        applicationSourceSection={ApplicationSourceSection.PROPERTY_CONFIGURATOR}
                    />
                    <Button
                        variant="none_secondary"
                        size="small"
                        onClick={() => propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_OFFER_CLICK)}
                    >
                        Zobacz szczegóły
                    </Button>
                </div>
            </div>
        </a>
    );
};

const wrapper = (theme: Theme) => css`
    ${flex()};
    ${p(1)};
    ${pr(2)};
    ${backgroundColor("white")};
    ${borderRadius(2)};
    gap: ${calculateRemSize(4)};
    ${elevation(1)};
    overflow: hidden;
    min-height: 262px;
    max-height: fit-content;

    @media (max-width: ${theme.breakpoints.lg}) {
        ${flexDirection("column")};
        gap: ${calculateRemSize(3)};
        ${p(2)};
        ${pt(1)};
        min-height: initial;
        max-height: initial;
    }

    &,
    :hover,
    :active,
    :focus {
        color: initial;
    }
`;

const propertyTextContainer = (theme: Theme) => css`
    ${flex()};
    ${flexDirection("column")};
    ${mv(1)};
    ${w100};

    @media (max-width: ${theme.breakpoints.lg}) {
        ${mb(1)};
    }

    > h5 {
        line-height: ${calculateRemSize(4)};
    }
`;

const buttonsWrapper = (theme: Theme) => css`
    margin-top: auto;
    ${flex()};
    gap: ${calculateRemSize(2)};

    > button:last-of-type {
        max-height: ${calculateRemSize(5)};
    }

    > button:first-of-type {
        max-width: 247px;
    }

    @media (max-width: ${theme.breakpoints.lg}) {
        > button:first-of-type {
            max-width: initial;
        }
    }

    @media (max-width: ${theme.breakpoints.lg}) {
        ${flexDirection("column")};
    }
`;

const specsContainer = css`
    ${flex("center")};
    gap: ${calculateRemSize(2)};
    ${mb(3)};
    ${mt(2.5)};
`;

interface ImageWrapperProps {
    hasImage: boolean;
}

const ImageWrapper = styled.div<ImageWrapperProps>`
    width: 100%;
    max-width: 417px;
    height: 246px;
    ${flexAbsoluteCenter};
    gap: ${calculateRemSize(1)};
    position: relative;

    > img {
        max-height: 100%;
        max-width: 100%;
    }

    > div {
        position: absolute;
        top: 50%;
        translate: 0 -50%;
    }

    > div:first-of-type {
        left: 0;
    }

    > div:last-of-type {
        right: 0;
    }

    ${(props) =>
        !props.hasImage &&
        css`
            height: calc(100% + ${calculateRemSize(2)}) !important;
            width: calc(100% + ${calculateRemSize(1)}) !important;
            margin-top: -${calculateRemSize(1)};
            margin-left: -${calculateRemSize(1)};

            @media (max-width: ${props.theme.breakpoints.lg}) {
                min-height: 204px;
                height: 204px;
                max-height: 204px;
                min-width: calc(100% + ${calculateRemSize(4)}) !important;
                max-width: initial;
                margin-top: -${calculateRemSize(1)};
                margin-left: -${calculateRemSize(2)};
            }
        `};

    @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
        width: 100%;
        max-width: 100%;
        max-height: 204px;

        > img {
            max-width: 100%;
        }
    }
`;

const tooltipStyle = (theme: Theme) => css`
    ${flexAbsoluteCenter};
    display: none;
    ${p(0.5)};
    color: ${theme.colors.gray[700]};
    ${borderRadius(1)};
    position: absolute;
    left: 0;
    top: 0;
    translate: 0 -100%;
    background-color: #fff;
    ${elevation(1)};
    white-space: nowrap;
    z-index: 1;
`;

const iconContainer = css`
    ${flexAbsoluteCenter};
    position: relative;
    gap: ${calculateRemSize(1)};

    :hover div {
        display: flex;
    }
`;
import {useMemo, useState} from "react";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import {isAfter, parseISO} from "date-fns";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {
    backgroundColor,
    borderRadius,
    calculateRemSize,
    flex,
    flexAbsoluteCenter,
    flexDirection,
    mb,
    ml,
    mr,
    mt,
    mv,
    p,
    pb,
    pr,
    pt,
    w100
} from "@pg-design/helpers-css";
import {CalendarCheckIcon, ChevronLeftIcon, ChevronRightIcon, FloorIcon} from "@pg-design/icons-module";
import {Placeholder} from "@pg-design/image-module";
import {Highlight, Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, dateTimeFormat, formatDate, pluralize, priceFormat} from "@pg-mono/string-utils";

import {ApplicationSourceSection} from "../../application/utils/ApplicationSource";
import {PropertyListApplicationButton} from "../../property_list/components/property_list_application_button/PropertyListApplicationButton";
import {GTMEvent, propertyConfiguratorAnalytics} from "../analytics/events";
import {IPropertyConfiguratorProperty} from "../types/IPropertyConfiguratorProperty";
import {AdditionalAreaTypeNames} from "../types/RoomType";

interface IOwnProps {
    property: IPropertyConfiguratorProperty;
}

interface IProps extends IOwnProps {}

export const HorizontalPropertyBox = (props: IProps) => {
    const {property} = props;
    const roomsPluralize = pluralize(["pokój", "pokoje", "pokoi"]);
    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: property.offer.vendor.slug,
        offerSlug: property.offer.slug,
        offerId: property.offer.id,
        propertyId: property.id
    });

    const mappedAdditionalAreas = useMemo(() => {
        if (!property.additional_areas.length) {
            return "";
        }

        const additionalAreas = property.additional_areas
            .filter((area) => !!area.area)
            .map((area) => `${AdditionalAreaTypeNames[area.type - 1]} ${area.area}m²`);
        return ` + ${additionalAreas.join(" + ")}`;
    }, []);

    const externalPlanUrl = property?.external_plan_url;
    const mainImage = externalPlanUrl || property.plan_image?.p_img_375x250;
    const [imageIndex, setImageIndex] = useState(0);

    const gallery = useMemo(() => {
        if (!property.gallery.length && !mainImage) {
            return [];
        }
        if (!property.gallery.length) {
            return [mainImage];
        }
        return [mainImage, ...property.gallery.map((image) => image.image.g_img_500)];
    }, []);

    const handleIndexChange = (value: number) => {
        if (imageIndex + value > gallery.length - 1) {
            return setImageIndex(0);
        }
        if (imageIndex + value === -1) {
            return setImageIndex(gallery.length - 1);
        }
        setImageIndex((prev) => prev + value);
    };

    const isBuild = isAfter(new Date(), parseISO(property.offer.construction_date_range?.upper as string));
    const date = formatDate(property.offer.construction_date_range?.upper, dateTimeFormat.quarter);

    return (
        <a href={propertyLink} target="_blank" css={wrapper}>
            <ImageWrapper hasImage={!!mainImage}>
                {gallery.length > 1 && (
                    <ChevronLeftIcon
                        onClick={(e) => {
                            e.preventDefault();
                            handleIndexChange(-1);
                        }}
                        wrapperSize="2.4"
                        size="2.4"
                        wrapperColor="transparent"
                    />
                )}
                {!!gallery.length ? <img src={gallery[imageIndex]} alt="plan mieszkania" /> : <Placeholder />}
                {gallery.length > 1 && (
                    <ChevronRightIcon
                        onClick={(e) => {
                            e.preventDefault();
                            handleIndexChange(1);
                        }}
                        wrapperSize="2.4"
                        size="2.4"
                        wrapperColor="transparent"
                    />
                )}
            </ImageWrapper>
            <div css={propertyTextContainer}>
                <Text variant="headline_5" css={mb(0.5)}>
                    {property.rooms} {roomsPluralize(property.rooms)}, {areaFormat(property.area)}
                    {mappedAdditionalAreas}
                </Text>
                <Text variant="info_txt_1">{property.offer.name}</Text>
                <Text variant="info_txt_1">{property.offer.address}</Text>
                <div css={specsContainer}>
                    <div css={iconContainer}>
                        <CalendarCheckIcon wrapperSize="2.4" size="2.4" wrapperColor="transparent" />
                        <Text variant="info_txt_1" css={[ml(0.5), mr(2)]}>
                            {isBuild ? "Gotowe do odbioru" : date}
                        </Text>
                        <div css={tooltipStyle}>Termin oddania: {isBuild ? "Gotowe do odbioru" : date}</div>
                    </div>
                    <div css={iconContainer}>
                        <FloorIcon wrapperSize="2.4" size="2.4" wrapperColor="transparent" />
                        <Text variant="info_txt_1" css={ml(0.5)}>
                            {property.floor || "Parter"}
                        </Text>
                        <div css={tooltipStyle}>Piętro: {property.floor || "Parter"}</div>
                    </div>
                </div>
                {!!property.price && (
                    <Text variant="info_txt_1" css={[pb(2)]}>
                        <Highlight>
                            <Text variant="headline_1" as="span">
                                {priceFormat(property.price)}&nbsp;
                            </Text>
                        </Highlight>
                    </Text>
                )}
                <div css={buttonsWrapper}>
                    <PropertyListApplicationButton
                        property={property}
                        isAutoresponderEnabled={property.offer.configuration.is_autoresponder_enabled}
                        vendorId={property.offer.vendor.id}
                        isInPropertyConfigurator
                        applicationSourceSection={ApplicationSourceSection.PROPERTY_CONFIGURATOR}
                    />
                    <Button
                        variant="none_secondary"
                        size="small"
                        onClick={() => propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_OFFER_CLICK)}
                    >
                        Zobacz szczegóły
                    </Button>
                </div>
            </div>
        </a>
    );
};

const wrapper = (theme: Theme) => css`
    ${flex()};
    ${p(1)};
    ${pr(2)};
    ${backgroundColor("white")};
    ${borderRadius(2)};
    gap: ${calculateRemSize(4)};
    ${elevation(1)};
    overflow: hidden;
    min-height: 262px;
    max-height: fit-content;

    @media (max-width: ${theme.breakpoints.lg}) {
        ${flexDirection("column")};
        gap: ${calculateRemSize(3)};
        ${p(2)};
        ${pt(1)};
        min-height: initial;
        max-height: initial;
    }

    &,
    :hover,
    :active,
    :focus {
        color: initial;
    }
`;

const propertyTextContainer = (theme: Theme) => css`
    ${flex()};
    ${flexDirection("column")};
    ${mv(1)};
    ${w100};

    @media (max-width: ${theme.breakpoints.lg}) {
        ${mb(1)};
    }

    > h5 {
        line-height: ${calculateRemSize(4)};
    }
`;

const buttonsWrapper = (theme: Theme) => css`
    margin-top: auto;
    ${flex()};
    gap: ${calculateRemSize(2)};

    > button:last-of-type {
        max-height: ${calculateRemSize(5)};
    }

    > button:first-of-type {
        max-width: 247px;
    }

    @media (max-width: ${theme.breakpoints.lg}) {
        > button:first-of-type {
            max-width: initial;
        }
    }

    @media (max-width: ${theme.breakpoints.lg}) {
        ${flexDirection("column")};
    }
`;

const specsContainer = css`
    ${flex("center")};
    gap: ${calculateRemSize(2)};
    ${mb(3)};
    ${mt(2.5)};
`;

interface ImageWrapperProps {
    hasImage: boolean;
}

const ImageWrapper = styled.div<ImageWrapperProps>`
    width: 100%;
    max-width: 417px;
    height: 246px;
    ${flexAbsoluteCenter};
    gap: ${calculateRemSize(1)};
    position: relative;

    > img {
        max-height: 100%;
        max-width: 100%;
    }

    > div {
        position: absolute;
        top: 50%;
        translate: 0 -50%;
    }

    > div:first-of-type {
        left: 0;
    }

    > div:last-of-type {
        right: 0;
    }

    ${(props) =>
        !props.hasImage &&
        css`
            height: calc(100% + ${calculateRemSize(2)}) !important;
            width: calc(100% + ${calculateRemSize(1)}) !important;
            margin-top: -${calculateRemSize(1)};
            margin-left: -${calculateRemSize(1)};

            @media (max-width: ${props.theme.breakpoints.lg}) {
                min-height: 204px;
                height: 204px;
                max-height: 204px;
                min-width: calc(100% + ${calculateRemSize(4)}) !important;
                max-width: initial;
                margin-top: -${calculateRemSize(1)};
                margin-left: -${calculateRemSize(2)};
            }
        `};

    @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
        width: 100%;
        max-width: 100%;
        max-height: 204px;

        > img {
            max-width: 100%;
        }
    }
`;

const tooltipStyle = (theme: Theme) => css`
    ${flexAbsoluteCenter};
    display: none;
    ${p(0.5)};
    color: ${theme.colors.gray[700]};
    ${borderRadius(1)};
    position: absolute;
    left: 0;
    top: 0;
    translate: 0 -100%;
    background-color: #fff;
    ${elevation(1)};
    white-space: nowrap;
    z-index: 1;
`;

const iconContainer = css`
    ${flexAbsoluteCenter};
    position: relative;
    gap: ${calculateRemSize(1)};

    :hover div {
        display: flex;
    }
`;
